import { useAuth0 } from '@auth0/auth0-react';
import { Article, Assignment, ContentCopy, ControlPointDuplicate, DownloadForOffline, Info, ListAlt, OpenInNew, ThumbUp } from '@mui/icons-material';
import { TabContext, TabPanel } from '@mui/lab';
import {
    Box,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    IconButton,
    Link,
    MenuItem,
    Paper,
    Select,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CloneQuoteDialog, usePrompt } from '../../../Views';
import { QuoteLineItemsView } from '../../../Views/Quote/LineItems';
import { CloneQuoteDto, CustomerDto, LineItemDto, QuoteDto, RegionDto, UserDto } from '../../../dtos';
import { AppraisalType } from '../../../dtos/generated/AppraisalType';
import {
    useArchiveQuoteDeleteByIdMutation,
    useCreateOrderQuickCreateByIdMutation,
    useCreateQuoteCloneMutation,
    useCreateQuoteCopyMutation,
    useCreateQuotePdfMutation,
    useGetContactQuery,
    useGetJobQuery,
    useGetRegionQuery,
    useGetUserQuery,
    useLazyGetUserAuth0UserIdByIdQuery,
    useUpdateQuoteAcceptMutation,
    useGetQuoteMaxExpirationDateQuery,
} from '../../../store/generated/generatedApi';
import { LineItemTypes, QuoteType, countries, emptyGuid, formatDate, quoteTypes } from '../../../util';
import { AddressLookup, ClearableSelect, ContactSelect, CustomerSelect, CustomerStatusAlert, DatePicker, SiteSelect } from '../../CommonInputs';
import { SlimFormHeader } from '../../Core/SlimFormHeader';
import {
    FormInput,
    FormNumberInput,
    FormSection,
    IFormProps,
    LoadingIndicator,
    useFailedActionSnackbar,
    useSuccessfulActionSnackbar,
} from '../../CoreLib/library';
import { CustomerQuickAddButton } from '../../QuickAdds';
import { AcceptQuoteLineItemsDialog } from './AcceptQuoteLineItemsDialog';
import { QuoteAttachmentsSection } from './QuoteAttachmentsSection';
import { useQuoteForm } from './useQuoteForm';
import { StateAutocomplete } from '../../CommonInputs/StateAutocomplete';
import { IEntityAutocomplete } from '../../CommonInputs/IEntityAutocomplete';

export interface IQuoteFormProps extends IFormProps<QuoteDto> {
    customer?: CustomerDto;
    onAfterSuccessfulCreate?: (quote: QuoteDto) => void;
}

export const QuoteForm: FC<IQuoteFormProps> = (props) => {
    const { isLoading, initValues, customer, onAfterSuccessfulCreate } = props;
    const { tab, type, appraisalType } = useParams();
    const [selectedTab, setSelectedTab] = useState(tab ?? '');
    const { user, isAuthenticated } = useAuth0();
    const navigate = useNavigate();
    const [isAcceptLineItemsDialogOpen, setIsAcceptLineItemsDialogOpen] = useState(false);

    const {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        formIsActive,
        formQuoteNumber,
        formStatus,
        formQuoteDate,
        formExpirationDate,
        formCustomer,
        formContact,
        formEffectiveOn,
        formPoNumber,
        formProjectName,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formDirections,
        formMiles,
        formMinutes,
        formToOrFromJob,
        formSalesPerson,
        formMemo,
        formType,
        formAppraisalType,
        formJobName,
        formSiteName,
        formRegion,
        formCustomerJobNumber,
        formSite,
        formIsTaxable,
        handleIsActiveChange,
        handleQuoteNumberChange,
        handleQuoteDateChange,
        handleExpirationDateChange,
        handleCustomerChange,
        handleContactChange,
        handleEffectiveOnDateChange,
        handlePoNumberChange,
        handleCustomerJobNumberChange,
        handleProjectNameChange,
        handleAddressLine1Change,
        handleAddressLine2Change,
        handleCityChange,
        handleStateChange,
        handleZipCodeChange,
        handleCountryChange,
        handleDirectionsChange,
        handleMilesChange,
        handleMinutesChange,
        handleToOrFromJobChange,
        handleSalesPersonChange,
        handleMemoChange,
        handleFormTypeChange,
        handleSiteNameChange,
        handleRegionChange,
        handleSiteChange,
        handleIsTaxableChange,
        setFormType,
        setFormAppraisalType,
        handleJobNameChange,
        setFormSalesPerson,
        quoteLabel,
        isMasterPriceListQuote,
        getCurrentFormValues,
        pendingAttachmentFiles,
        pendingRemovalAttachmentIds,
        addPendingAttachmentFiles,
        removePendingAttachmentFile,
        stageAttachmentRemoval,
        attachmentSaveError,
        attachmentSyncInProgress,
        pendingPostCreateQuote,
        clearPendingPostCreateQuote,
    } = useQuoteForm(props);

    usePrompt('Are you sure you want to leave this page?\nUnsaved changes will be lost.', isFormDirty());

    // Ensure the onAfterSuccessfulCreate navigation happens after usePrompt, that way the exit prompt is removed when the form is no longer dirty
    useEffect(() => {
        if (!pendingPostCreateQuote || !onAfterSuccessfulCreate) {
            return;
        }
        onAfterSuccessfulCreate(pendingPostCreateQuote);
        clearPendingPostCreateQuote();
    }, [pendingPostCreateQuote, onAfterSuccessfulCreate, clearPendingPostCreateQuote]);

    const [getUserData, { data: currentUser }] = useLazyGetUserAuth0UserIdByIdQuery();

    const { data: maxExpirationDateDetails, isLoading: quoteExpirationDateLoading } = useGetQuoteMaxExpirationDateQuery({ id: initValues?.id ?? emptyGuid });
    const quoteMaxExpirationDate = useMemo(() => {
        if (!maxExpirationDateDetails && (initValues?.id === emptyGuid || initValues?.id === undefined)) {
            return (undefined);
        } else if (!maxExpirationDateDetails && (initValues?.id !== emptyGuid || initValues?.id !== undefined)) {
            return (formExpirationDate ?? undefined);
        } else {
            return (maxExpirationDateDetails?.maxExpirationDate ?? formExpirationDate ?? undefined);
        }
    }, [maxExpirationDateDetails, formExpirationDate, initValues?.id]);

    const { isLoading: contactsLoading } = useGetContactQuery({
        customerId: formCustomer?.id ?? '',
        searchText: '',
        sorting: ['CONTACT_NAME+'],
        page: 0,
        pageSize: 100000,
        includeInactive: false,
    });
    const { data: jobs } = useGetJobQuery({
        quoteId: initValues?.id,
        searchText: '',
        sorting: ['JOB_NUMBER+'],
        page: 0,
        pageSize: 100000,
        includeInactive: false,
    });
    const { data: users, isLoading: usersLoading } = useGetUserQuery({
        searchText: '',
        sorting: ['FIRST_NAME+'],
        page: 0,
        pageSize: 100000,
        includeInactive: false,
    });

    const { currentData: regions } = useGetRegionQuery({ searchText: '', sorting: ['NAME+'], page: 0, pageSize: 100000, includeInactive: false });

    const [getQuotePdf, { isSuccess, isError, error: pdfGenerationError, isLoading: pdfLoading, reset: resetCreatePDf }] = useCreateQuotePdfMutation();
    const [acceptQuote, { isSuccess: isQuoteAcceptSuccess, isError: isQuoteAcceptError, isLoading: isAcceptingQuote, reset: resetAcceptQuote }] =
        useUpdateQuoteAcceptMutation();
    const [isCloneQuoteDialogOpen, setIsCloneQuoteDialogOpen] = useState(false);
    const [isCopyQuoteDialogOpen, setIsCopyQuoteDialogOpen] = useState(false);
    const [
        duplicateQuote,
        {
            data: duplicatedQuote,
            isSuccess: isQuoteDuplicateSuccess,
            isError: isQuoteDuplicateSuccessError,
            isLoading: isDuplicatingQuote,
            reset: resetDuplicateQuote,
        },
    ] = useCreateQuoteCloneMutation();
    useSuccessfulActionSnackbar('cloned', 'quote', isQuoteDuplicateSuccess, resetDuplicateQuote);
    useFailedActionSnackbar('clone', 'quote', isQuoteDuplicateSuccessError, resetDuplicateQuote);

    const [
        copyQuote,
        {
            data: copiedQuote,
            isSuccess: isQuoteCopySuccess,
            isError: isQuoteCopySuccessError,
            isLoading: isCopyingQuote,
            reset: resetCopyQuote,
        },
    ] = useCreateQuoteCopyMutation();
    useSuccessfulActionSnackbar('copied', 'quote', isQuoteCopySuccess, resetCopyQuote);
    useFailedActionSnackbar('copy', 'quote', isQuoteCopySuccessError, resetCopyQuote);

    const [
        quickCreateOrder,
        { data: order, isSuccess: quickCreateSuccess, isError: quickCreateError, isLoading: quickCreateOrderLoading, reset: resetQuickCreate },
    ] = useCreateOrderQuickCreateByIdMutation();
    useSuccessfulActionSnackbar('created', 'order', quickCreateSuccess, resetQuickCreate);
    useFailedActionSnackbar('create', 'order', quickCreateError, resetQuickCreate);

    useSuccessfulActionSnackbar('created', 'quote PDF', isSuccess, resetCreatePDf);
    useFailedActionSnackbar('create', 'quote PDF', isError, resetCreatePDf, pdfGenerationError);
    useSuccessfulActionSnackbar('accepted', 'quote', isQuoteAcceptSuccess, resetAcceptQuote);
    useFailedActionSnackbar('accept', 'quote', isQuoteAcceptError, resetAcceptQuote);

    useEffect(() => {
        if (isQuoteAcceptSuccess) {
            if (jobs) {
                if (jobs.totalQueryResults > 0) {
                    navigate(`/job/${jobs?.pageResults[0].id}`);
                }
            }
        }
    }, [isQuoteAcceptSuccess, jobs, navigate]);

    useEffect(() => {
        if (quickCreateSuccess && order) {
            window.open(`/job/${order?.jobId}/order/${order.id}`, '_blank');
        }
    }, [isSuccess, quickCreateSuccess, order]);

    useEffect(() => {
        if (isQuoteDuplicateSuccess && duplicatedQuote) {
            resetDuplicateQuote();
            window.open(`/quote/${duplicatedQuote?.id}`, '_blank');
        }
    }, [isQuoteDuplicateSuccess, duplicatedQuote, resetDuplicateQuote]);

    useEffect(() => {
        if (isQuoteCopySuccess && copiedQuote) {
            resetCopyQuote();
            window.open(`/quote/${copiedQuote?.id}`, '_blank');
        }
    }, [isQuoteCopySuccess, copiedQuote, resetCopyQuote]);

    useEffect(() => {
        // Catch the case where the user navigates to a quote form with a type that is not supported
        if (type) {
            if (type !== QuoteType.Standard && type !== QuoteType.FOB) {
                navigate(`/quote/create/${QuoteType.Standard}/${formAppraisalType}`);
            }
            else {
                setFormType(type);
            }
        }

        if (appraisalType) {
            const parsedInt = parseInt(appraisalType);
            if (!isNaN(parsedInt)) {
                setFormAppraisalType(parsedInt as AppraisalType);
            }
        }
    }, [type, setFormType, appraisalType, setFormAppraisalType, navigate, formAppraisalType]);

    useEffect(() => {
        if (currentUser && !formSalesPerson) {
            setFormSalesPerson(currentUser);
        }
        if (!currentUser && isAuthenticated && (!initValues?.id || initValues?.id === emptyGuid)) {
            if (user?.sub) {
                getUserData({ id: user?.sub });
            }
        }
    }, [user, getUserData, isAuthenticated, initValues?.id, currentUser, formSalesPerson, setFormSalesPerson]);

    const displayName = useMemo(() => {
        const jobName = formJobName ? `${formJobName} - ` : '';
        const customerNumber = formCustomer?.customerNumber ? `${formCustomer.customerNumber}: ` : '';

        return (
            initValues?.id
                ? `${jobName}${customerNumber}${formCustomer?.name} (${quoteLabel} #${initValues?.quoteNumber})`
                : `New ${quoteLabel}`
        ).trim();
    }, [formCustomer?.name, formCustomer?.customerNumber, formJobName, initValues?.id, initValues?.quoteNumber, quoteLabel]);

    const quoteTabTitle = useMemo(() => {
        if (!initValues?.id) return `New ${quoteLabel}`;
        const customerNumber = formCustomer?.customerNumber ?? '';
        return `${quoteLabel} #${initValues?.quoteNumber}: Cust #${customerNumber}`;
    }, [formCustomer?.customerNumber, initValues?.id, initValues?.quoteNumber, quoteLabel]);

    const currentId = useMemo(() => {
        return props.initValues?.id;
    }, [props.initValues?.id]);

    const generatePdf = useCallback(() => {
        if (!pdfLoading && currentId) {
            getQuotePdf({ id: currentId }).then((response: any) => {
                if (response?.data) {
                    window.open(response?.data.url, '_blank');
                }
            });
        }
    }, [currentId, getQuotePdf, pdfLoading]);

    const handleAcceptClicked = useCallback(() => {
        const hasAmendedLineItems = initValues?.hasAmendedLineItems;
        if (!isAcceptingQuote && currentId) {
            if (!hasAmendedLineItems) {
                acceptQuote({ params: { id: currentId }, payload: [] });
            } else {
                setIsAcceptLineItemsDialogOpen(true);
            }
        }
    }, [initValues?.hasAmendedLineItems, isAcceptingQuote, currentId, acceptQuote]);

    const handleCloneQuote = useCallback(
        (dto: CloneQuoteDto, isCopy: boolean) => {
            if (isCopy) {
                setIsCopyQuoteDialogOpen(false);
                if (initValues?.id) {
                    copyQuote({ ...dto, id: initValues.id });
                }
            } else {
                setIsCloneQuoteDialogOpen(false);
                if (initValues?.id) {
                    duplicateQuote({ ...dto, id: initValues.id });
                }
            }
        },
        [copyQuote, duplicateQuote, initValues]
    );

    const isCopyOfMasterPriceListQuote = useMemo(() => {
        return initValues?.copiedFromMPLQuoteNumber;
    }, [initValues?.copiedFromMPLQuoteNumber]);

    const isAccepted = useMemo(() => {
        return !!props.initValues?.acceptedOn;
    }, [props.initValues]);

    const handleQuickInputOrder = useCallback(() => {
        if (initValues?.jobId) {
            quickCreateOrder({ jobId: initValues?.jobId });
        }
    }, [initValues?.jobId, quickCreateOrder]);

    const AdditionalHeaderItems = useMemo(() => {
        return (
            <>
                <Tooltip title={`Accept`}>
                    {!isAcceptingQuote ? (
                        <Box>
                            <IconButton onClick={handleAcceptClicked} disabled={!initValues || isAccepted || isAcceptingQuote}>
                                <ThumbUp />
                            </IconButton>
                        </Box>
                    ) : (
                        <CircularProgress />
                    )}
                </Tooltip>
                {currentId && (
                    <Tooltip title={`Clone`}>
                        <span>
                            <Box>
                                <IconButton
                                    onClick={() => {
                                        if (initValues?.jobId) {
                                            setIsCloneQuoteDialogOpen(true);
                                        }
                                    }}
                                    disabled={isLoading || isDuplicatingQuote || initValues?.clonedQuoteId !== null}>
                                    <ControlPointDuplicate />
                                </IconButton>
                            </Box>
                        </span>
                    </Tooltip>
                )}
                <Tooltip title={`Copy`}>
                    {!isAcceptingQuote ? (
                        <Box>
                            <IconButton onClick={() => {
                                setIsCopyQuoteDialogOpen(true);

                            }} disabled={!initValues || isCopyingQuote}>
                                <ContentCopy />
                            </IconButton>
                        </Box>
                    ) : (
                        <CircularProgress />
                    )}
                </Tooltip >
                {formAppraisalType !== AppraisalType.Estimate && (
                    <Tooltip title='Download PDF'>
                        {!pdfLoading ? (
                            <Box>
                                <IconButton onClick={generatePdf} disabled={!initValues || pdfLoading}>
                                    <DownloadForOffline />
                                </IconButton>
                            </Box>
                        ) : (
                            <CircularProgress />
                        )}
                    </Tooltip>
                )}
                <Divider flexItem orientation='vertical' sx={{ mx: 1 }} />
                {!isMasterPriceListQuote &&
                    <>
                        <Tooltip title={`Quick Input Order`}>
                            {!isAcceptingQuote ? (
                                <Box>
                                    <IconButton onClick={handleQuickInputOrder} disabled={!initValues || quickCreateOrderLoading || !initValues?.isCurrentForJob}>
                                        <ListAlt />
                                    </IconButton>
                                </Box>
                            ) : (
                                <CircularProgress />
                            )}
                        </Tooltip>
                        <Tooltip title={`View Job`}>
                            {!isAcceptingQuote ? (
                                <Box>
                                    <IconButton
                                        onClick={() => {
                                            navigate(`/job/${initValues?.jobId}`);
                                        }}
                                        disabled={!initValues}>
                                        <Article />
                                    </IconButton>
                                </Box>
                            ) : (
                                <CircularProgress />
                            )}
                        </Tooltip>
                    </>
                }
            </>
        );
    }, [isAcceptingQuote, isMasterPriceListQuote, handleAcceptClicked, initValues, isAccepted, currentId, isLoading, isDuplicatingQuote, formAppraisalType, pdfLoading, generatePdf, handleQuickInputOrder, quickCreateOrderLoading, isCopyingQuote, navigate]);

    const AdditionalMenuItems = useMemo(() => {
        const items = [
            {
                name: 'Accept Quote',
                onClick: handleAcceptClicked,
                disabled: !props.initValues || isAccepted || isAcceptingQuote,
            },
        ];

        if (formAppraisalType === AppraisalType.Quote) {
            items.push({
                name: 'Download PDF',
                onClick: generatePdf,
                disabled: !props.initValues || pdfLoading,
            });
        }
        return items;
    }, [formAppraisalType, handleAcceptClicked, props.initValues, isAccepted, isAcceptingQuote, generatePdf, pdfLoading]);

    const handleConfirmAcceptQuoteLineItems = useCallback(
        (lineItems: LineItemDto[]) => {
            setIsAcceptLineItemsDialogOpen(false);
            if (currentId) {
                const lineItemIds = lineItems.map((x) => x.id);
                acceptQuote({ params: { id: currentId }, payload: lineItemIds });
            }
        },
        [acceptQuote, currentId]
    );

    const hasMaterialLineItem = useMemo(() => {
        return initValues?.lineItems?.some((x) => x.type === LineItemTypes.Material) ?? false;
    }, [initValues?.lineItems]);

    const handleNewCustomerAdded = useCallback(
        (newCustomer: CustomerDto) => {
            handleCustomerChange(newCustomer);
        },
        [handleCustomerChange]
    );

    const currentFormValues = useMemo(() => {
        return { ...getCurrentFormValues() } as QuoteDto;
    }, [getCurrentFormValues]);

    if (isLoading || contactsLoading || usersLoading || quoteExpirationDateLoading) {
        return <LoadingIndicator />;
    }

    return (
        <>
            {formEffectiveOn && (
                <CloneQuoteDialog
                    open={isCloneQuoteDialogOpen}
                    onConfirm={(dto) => handleCloneQuote(dto, false)}
                    onClose={() => setIsCloneQuoteDialogOpen(false)}
                    quoteEffectiveOn={new Date(formEffectiveOn)}
                    quoteExpirationDate={formExpirationDate ? new Date(formExpirationDate) : undefined}
                    isCopying={false}
                />
            )}
            {formEffectiveOn && (
                <CloneQuoteDialog
                    open={isCopyQuoteDialogOpen}
                    onConfirm={(dto) => handleCloneQuote(dto, true)}
                    onClose={() => setIsCopyQuoteDialogOpen(false)}
                    quoteEffectiveOn={new Date(formEffectiveOn)}
                    quoteExpirationDate={formExpirationDate ? new Date(formExpirationDate) : undefined}
                    quote={!isMasterPriceListQuote ? currentFormValues : undefined}
                    isCopying={true}
                />
            )}
            {initValues && (
                <AcceptQuoteLineItemsDialog
                    open={isAcceptLineItemsDialogOpen}
                    onClose={() => setIsAcceptLineItemsDialogOpen(false)}
                    onConfirm={(selectedLineItems) => handleConfirmAcceptQuoteLineItems(selectedLineItems)}
                    quote={initValues}
                />
            )}
            <SlimFormHeader
                objectName={quoteLabel}
                permissionsTypeName={'quote'}
                icon={<Assignment />}
                title={displayName}
                tabTitle={quoteTabTitle}
                titleMaxWidth="60vw"
                breadcrumbs={
                    customer
                        ? [
                            { label: 'Home', navLink: '/' },
                            { label: 'Customers', navLink: '/customers' },
                            { label: customer?.name, navLink: `/customer/${customer?.id}/dashboard` },
                        ]
                        : [
                            { label: 'Home', navLink: '/' },
                            { label: `Quotes`, navLink: '/quotes' },
                        ]
                }
                isActive={formIsActive}
                handleIsActiveChange={handleIsActiveChange}
                id={currentId}
                isLoading={isLoading}
                isFormDirty={isFormDirty}
                handleCancel={handleCancel}
                handleSave={handleSave}
                entityNameSingular={'quote'}
                deleteMutation={useArchiveQuoteDeleteByIdMutation}
                additionalHeaderItems={AdditionalHeaderItems}
                additionalMenuItems={AdditionalMenuItems}
            />
            <Grid container direction='column' spacing={2}>
                {initValues?.id && !initValues?.acceptedOn && jobs?.totalQueryResults && !isMasterPriceListQuote && (
                    <Grid item mx={2}>
                        <Paper square variant='outlined'>
                            {jobs.pageResults.map((x) => {
                                return (
                                    <Grid key={x.id} container direction='row' justifyContent='center' p={2}>
                                        <Typography>
                                            <Link
                                                href={`/job/${x?.id}`}
                                                variant='body2'
                                                onClick={() => {
                                                    navigate(`/job/${x?.id}`);
                                                }}>
                                                Job #{x.jobNumber}
                                            </Link>{' '}
                                            is pending this {quoteLabel}'s approval.
                                        </Typography>
                                    </Grid>
                                );
                            })}
                        </Paper>
                    </Grid>
                )}
                <Grid item mx={2}>
                    <FormSection>
                        <TabContext value={selectedTab}>
                            <Box sx={{ borderBottom: '1px solid black' }}>
                                <Tabs
                                    value={selectedTab}
                                    onChange={(e, value) => setSelectedTab(value)}
                                    variant='fullWidth'
                                    textColor='primary'
                                    indicatorColor='primary'
                                    sx={{ width: initValues?.id !== undefined ? '100%' : '50%' }}>
                                    <Tab label={'GENERAL'} key={0} value={''} />
                                    {initValues?.id !== undefined && <Tab label={'Line Items'} key={1} value={'lineItems'} />}
                                </Tabs>
                            </Box>
                            <TabPanel value={''}>
                                <Grid item container direction='column' spacing={3}>
                                    <Grid item container direction='row' spacing={3}>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <FormNumberInput
                                                value={formQuoteNumber}
                                                onChange={handleQuoteNumberChange}
                                                label={`${quoteLabel} #`}
                                                name='customerNumber'
                                                fullWidth
                                                required
                                                disabled
                                            />
                                        </Grid>
                                        {isCopyOfMasterPriceListQuote && (
                                            <Grid item xs={12} sm={6} md={2}>
                                                <FormNumberInput
                                                    value={initValues?.copiedFromMPLQuoteNumber ?? 'Unknown'}
                                                    onChange={() => { }}
                                                    label={`Master Price List #`}
                                                    name='masterPriceListNumber'
                                                    fullWidth
                                                    required
                                                    disabled
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={6} md={2}>
                                            <FormControl error={!!fieldErrors.get('type')} fullWidth required disabled={initValues?.id !== undefined}>
                                                <FormLabel>{quoteLabel} Type</FormLabel>
                                                <Select value={formType} onChange={handleFormTypeChange}>
                                                    {quoteTypes.map((type) => (
                                                        <MenuItem key={type} value={type}>
                                                            {type}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <FormHelperText>{fieldErrors.get('type')}</FormHelperText>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <FormInput value={formStatus} onChange={() => { }} label={`${quoteLabel} Status`} fullWidth disabled required />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <DatePicker
                                                value={formQuoteDate}
                                                onChange={handleQuoteDateChange}
                                                error={!!fieldErrors.get('quoteDate')}
                                                label={`${quoteLabel} Date`}
                                                disabled={isAccepted}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <DatePicker
                                                value={formEffectiveOn}
                                                onChange={handleEffectiveOnDateChange}
                                                error={!!fieldErrors.get('effectiveOn')}
                                                label={`Effective On Date`}
                                                disabled={isAccepted || hasMaterialLineItem}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <FormControl fullWidth required>
                                                <Grid container spacing={2}>
                                                    <Grid item>
                                                        <FormLabel>Expiration Date</FormLabel>
                                                    </Grid>
                                                    <Grid item>
                                                        <Tooltip title={
                                                            <Box>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                                    Expiration Date Limited To: {quoteMaxExpirationDate ? formatDate(quoteMaxExpirationDate) : 'N/A'}
                                                                </Typography>
                                                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                                                    Limiting factors:
                                                                </Typography>
                                                                {maxExpirationDateDetails && (
                                                                    <>
                                                                        {maxExpirationDateDetails.quoteDeletedOnDate && (
                                                                            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                                                                • Quote was deleted on {formatDate(maxExpirationDateDetails.quoteDeletedOnDate)}
                                                                            </Typography>
                                                                        )}
                                                                        {maxExpirationDateDetails.clonedToQuote && (
                                                                            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                                                                • Cloned to Quote #{maxExpirationDateDetails.clonedToQuote.clonedToQuoteNumber} (effective {formatDate(maxExpirationDateDetails.clonedToQuote.effectiveOn)})
                                                                            </Typography>
                                                                        )}
                                                                        {maxExpirationDateDetails.inactiveMaterialPrices && maxExpirationDateDetails.inactiveMaterialPrices.inactiveMaterialPriceCount > 0 && (
                                                                            <Box>
                                                                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                                                                    • {maxExpirationDateDetails.inactiveMaterialPrices.inactiveMaterialPriceCount} inactive material price{maxExpirationDateDetails.inactiveMaterialPrices.inactiveMaterialPriceCount > 1 ? 's' : ''}:
                                                                                </Typography>
                                                                                {maxExpirationDateDetails.inactiveMaterialPrices.inactiveMaterialPriceLineItems.map((item) => (
                                                                                    <Typography key={item.materialPriceId} variant="caption" display="block" sx={{ ml: 2, mb: 0.25 }}>
                                                                                        - {item.materialPriceName}
                                                                                    </Typography>
                                                                                ))}
                                                                            </Box>
                                                                        )}
                                                                        {maxExpirationDateDetails.futureMaterialPriceChange && (
                                                                            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                                                                • Material price change: {maxExpirationDateDetails.futureMaterialPriceChange.materialPriceName} (effective {formatDate(maxExpirationDateDetails.futureMaterialPriceChange.effectiveOn)})
                                                                            </Typography>
                                                                        )}
                                                                        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                                                            • End of year: {formatDate(maxExpirationDateDetails.endOfYearDate)}
                                                                        </Typography>
                                                                    </>
                                                                )}
                                                            </Box>
                                                        }><Info /></Tooltip>
                                                    </Grid>
                                                </Grid>
                                                <DatePicker
                                                    value={formExpirationDate}
                                                    onChange={handleExpirationDateChange}
                                                    error={!!fieldErrors.get('expirationDate')}
                                                    maxDate={quoteMaxExpirationDate}
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                    <Grid item container direction='row' spacing={3} alignItems='center'>
                                        <Grid item xs={12} sm={6} md={3} display='flex'>
                                            <FormControl
                                                error={!!fieldErrors.get('customerId')}
                                                fullWidth
                                                required
                                                disabled={customer !== undefined}>
                                                <FormLabel>Customer</FormLabel>
                                                <CustomerSelect
                                                    value={formCustomer}
                                                    onChange={(e, value) => {
                                                        handleCustomerChange(value ?? null);
                                                    }}
                                                    disabled={customer !== undefined}
                                                    error={!!fieldErrors.get('customerId')}
                                                    errorMessage={fieldErrors.get('customerId')}
                                                    endAdornment={formCustomer ? <Link href={`/customer/${formCustomer?.id}`} target="_blank">
                                                        <IconButton disabled={!formCustomer}>
                                                            <OpenInNew />
                                                        </IconButton>
                                                    </Link> : null}
                                                    includeFormControl={false}
                                                />
                                                <FormHelperText>{fieldErrors.get('customerId')}</FormHelperText>
                                            </FormControl>
                                            <Box pt={3.5}>
                                                <CustomerQuickAddButton onAdd={handleNewCustomerAdded} />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3} display='flex'>
                                            <ContactSelect
                                                customerId={formCustomer?.id}
                                                selectedContactId={formContact?.id ?? ''}
                                                handleSelectedContactChange={handleContactChange}
                                                disabled={!formCustomer}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <FormControl fullWidth>
                                                <FormLabel>Salesperson</FormLabel>
                                                <IEntityAutocomplete
                                                    options={users?.pageResults}
                                                    onChange={(e, value) => {
                                                        handleSalesPersonChange(value ?? null);
                                                    }}
                                                    value={formSalesPerson}
                                                    getOptionLabel={(option: UserDto) => `${option.firstName} ${option.lastName}`}
                                                    isLoading={usersLoading}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <FormInput
                                                value={formPoNumber}
                                                onChange={(e) => handlePoNumberChange(e.target.value)}
                                                label='PO Number'
                                                fullWidth
                                                maxLength={30}
                                                required
                                                errorText={fieldErrors.get('poNumber')}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <FormInput
                                                value={formCustomerJobNumber}
                                                onChange={handleCustomerJobNumberChange}
                                                label='Job Number'
                                                fullWidth
                                            />
                                        </Grid>
                                        {(initValues?.clonedFromQuote || initValues?.clonedFromQuoteId || initValues?.clonedQuote || initValues?.clonedQuoteId) && (
                                            <Grid item xs={12}>
                                                <Paper square variant='outlined' sx={{ p: 2 }}>
                                                    <Grid container direction='column' spacing={1}>
                                                        <Grid item>
                                                            <Typography variant='subtitle2' sx={{ fontWeight: 'bold', mb: 1 }}>
                                                                Quote Cloning Information
                                                            </Typography>
                                                        </Grid>
                                                        {initValues?.clonedFromQuote && (
                                                            <Grid item container direction='row' alignItems='center' spacing={1}>
                                                                <Grid item>
                                                                    <Typography variant='body2'>
                                                                        Cloned from:
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Link
                                                                        href={`/quote/${initValues.clonedFromQuote.id}`}
                                                                        variant='body2'
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            window.open(`/quote/${initValues.clonedFromQuote?.id}`, '_blank');
                                                                        }}
                                                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                                    >
                                                                        Quote #{initValues.clonedFromQuote.quoteNumber}
                                                                        <OpenInNew sx={{ fontSize: 16 }} />
                                                                    </Link>
                                                                </Grid>
                                                            </Grid>
                                                        )}
                                                        {!initValues?.clonedFromQuote && initValues?.clonedFromQuoteId && (
                                                            <Grid item container direction='row' alignItems='center' spacing={1}>
                                                                <Grid item>
                                                                    <Typography variant='body2'>
                                                                        Cloned from:
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Link
                                                                        href={`/quote/${initValues.clonedFromQuoteId}`}
                                                                        variant='body2'
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            window.open(`/quote/${initValues.clonedFromQuoteId}`, '_blank');
                                                                        }}
                                                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                                    >
                                                                        Quote (ID: {initValues.clonedFromQuoteId.substring(0, 8)}...)
                                                                        <OpenInNew sx={{ fontSize: 16 }} />
                                                                    </Link>
                                                                </Grid>
                                                            </Grid>
                                                        )}
                                                        {initValues?.clonedQuote && (
                                                            <Grid item container direction='row' alignItems='center' spacing={1}>
                                                                <Grid item>
                                                                    <Typography variant='body2'>
                                                                        Cloned to:
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Link
                                                                        href={`/quote/${initValues.clonedQuote.id}`}
                                                                        variant='body2'
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            window.open(`/quote/${initValues.clonedQuote?.id}`, '_blank');
                                                                        }}
                                                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                                    >
                                                                        Quote #{initValues.clonedQuote.quoteNumber}
                                                                        <OpenInNew sx={{ fontSize: 16 }} />
                                                                    </Link>
                                                                </Grid>
                                                            </Grid>
                                                        )}
                                                        {!initValues?.clonedQuote && initValues?.clonedQuoteId && (
                                                            <Grid item container direction='row' alignItems='center' spacing={1}>
                                                                <Grid item>
                                                                    <Typography variant='body2'>
                                                                        Cloned to:
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Link
                                                                        href={`/quote/${initValues.clonedQuoteId}`}
                                                                        variant='body2'
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            window.open(`/quote/${initValues.clonedQuoteId}`, '_blank');
                                                                        }}
                                                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                                    >
                                                                        Quote (ID: {initValues.clonedQuoteId.substring(0, 8)}...)
                                                                        <OpenInNew sx={{ fontSize: 16 }} />
                                                                    </Link>
                                                                </Grid>
                                                            </Grid>
                                                        )}
                                                    </Grid>
                                                </Paper>
                                            </Grid>
                                        )}
                                        {formCustomer && (
                                            <Grid item xs={12}>
                                                <CustomerStatusAlert customer={formCustomer} />
                                            </Grid>
                                        )}
                                    </Grid>
                                    <Grid item container direction='row'>
                                        <Grid item>
                                            <Typography variant='h2'>Project</Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid item container direction='row' spacing={3} alignItems='center'>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <FormInput
                                                value={formProjectName}
                                                onChange={handleProjectNameChange}
                                                label='Project Name'
                                                fullWidth
                                            />
                                        </Grid>
                                        {!isMasterPriceListQuote && <Grid item xs={12} sm={6} md={3}>
                                            <FormInput
                                                value={formJobName}
                                                onChange={(event) => handleJobNameChange(event.target.value)}
                                                label='Job Name'
                                                fullWidth
                                            />
                                        </Grid>}
                                    </Grid>
                                    {!isMasterPriceListQuote &&
                                        <>
                                            <Grid item container direction='row' spacing={3}>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <AddressLookup
                                                        handleAddressLine1Change={handleAddressLine1Change}
                                                        handleCityChange={handleCityChange}
                                                        handleCountryChange={handleCountryChange}
                                                        handleStateChange={handleStateChange}
                                                        handleZipCodeChange={handleZipCodeChange}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <SiteSelect
                                                        selectedSiteId={formSite?.id ?? ''}
                                                        handleSelectedSiteChange={handleSiteChange}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <FormInput
                                                        value={formSiteName}
                                                        onChange={(event) => handleSiteNameChange(event)}
                                                        label='Site Name'
                                                        fullWidth
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <FormControl fullWidth>
                                                        <FormLabel>Region</FormLabel>
                                                        <IEntityAutocomplete
                                                            options={regions?.pageResults ?? []}
                                                            onChange={(_e, value) => {
                                                                handleRegionChange?.(value);
                                                            }}
                                                            value={formRegion}
                                                            getOptionLabel={(option: RegionDto) => option.name}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                            <Grid item container direction='row' spacing={3}>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <FormInput
                                                        value={formAddressLine1}
                                                        onChange={(e) => handleAddressLine1Change(e.target.value)}
                                                        label='Address Line 1'
                                                        name='addressLine1'
                                                        errorText={fieldErrors.get('addressLine1')}
                                                        fullWidth
                                                        required
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={2}>
                                                    <FormInput
                                                        value={formAddressLine2}
                                                        onChange={(e) => handleAddressLine2Change(e.target.value)}
                                                        label='Address Line 2'
                                                        name='addressLine2'
                                                        fullWidth
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={2}>
                                                    <FormInput
                                                        value={formCity}
                                                        onChange={(e) => handleCityChange(e.target.value)}
                                                        label='City'
                                                        name='city'
                                                        errorText={fieldErrors.get('city')}
                                                        fullWidth
                                                        required
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={2}>
                                                    <StateAutocomplete
                                                        error={!!fieldErrors.get('state')}
                                                        fullWidth
                                                        required
                                                        value={formState}
                                                        onChange={(event, value) => {
                                                            handleStateChange(value ?? '');
                                                        }}
                                                        fieldError={fieldErrors.get('state')}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={1.5}>
                                                    <FormInput
                                                        value={formZipCode}
                                                        onChange={(e) => handleZipCodeChange(e.target.value)}
                                                        label='Zip Code'
                                                        name='zipCode'
                                                        errorText={fieldErrors.get('zipCode')}
                                                        fullWidth
                                                        required
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={1.5}>
                                                    <FormControl error={!!fieldErrors.get('country')} fullWidth required>
                                                        <FormLabel>Country</FormLabel>
                                                        <ClearableSelect
                                                            value={formCountry ?? ''}
                                                            onChange={(e) => handleCountryChange(e.target.value as string)}
                                                            onClear={() => handleCountryChange('')}
                                                        >
                                                            {countries.map((country) => (
                                                                <MenuItem key={country} value={country}>
                                                                    {country}
                                                                </MenuItem>
                                                            ))}
                                                        </ClearableSelect>
                                                        <FormHelperText>{fieldErrors.get('country')}</FormHelperText>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                            <Grid item container direction='row' spacing={3}>
                                                <Grid item xs={12} sm={12} md={12}>
                                                    <TextField
                                                        value={formDirections}
                                                        onChange={(e) => handleDirectionsChange(e.target.value)}
                                                        label='Directions'
                                                        name='directions'
                                                        fullWidth
                                                        multiline
                                                        maxRows={5}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </>
                                    }
                                    <Grid item container direction='row' spacing={3} alignItems='center'>
                                        {!isMasterPriceListQuote &&
                                            <>
                                                <Grid item xs={12} sm={6} md={2}>
                                                    <FormNumberInput
                                                        value={formMiles}
                                                        name='miles'
                                                        onChange={handleMilesChange}
                                                        label='Miles'
                                                        fullWidth
                                                        inputProps={{ min: 0 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={2}>
                                                    <FormNumberInput
                                                        value={formMinutes}
                                                        name='minutes'
                                                        onChange={handleMinutesChange}
                                                        label='Minutes'
                                                        fullWidth
                                                        inputProps={{ min: 0 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={2}>
                                                    <FormInput
                                                        value={formToOrFromJob}
                                                        onChange={handleToOrFromJobChange}
                                                        label='To or from Job'
                                                        name='toOrFromJob'
                                                        fullWidth
                                                    />
                                                </Grid>
                                            </>}
                                        <Grid item xs={12} sm={6} md={2}>
                                            <FormControl fullWidth>
                                                <FormControlLabel control={<Checkbox checked={formIsTaxable} onChange={handleIsTaxableChange} />} label='Is Taxable' />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                    <Grid item container direction='row' spacing={3}>
                                        <Grid item xs={12}>
                                            <TextField
                                                value={formMemo}
                                                onChange={handleMemoChange}
                                                label='Quote Memo'
                                                fullWidth
                                                multiline
                                                minRows={3}
                                            />
                                        </Grid>
                                    </Grid>
                                    {initValues && initValues?.id !== emptyGuid && (
                                        <Grid item xs={12} sx={{ mt: 1 }}>
                                            <QuoteAttachmentsSection
                                                quoteId={initValues.id}
                                                pendingFiles={pendingAttachmentFiles}
                                                pendingRemovalIds={pendingRemovalAttachmentIds}
                                                onAddPendingFiles={addPendingAttachmentFiles}
                                                onRemovePendingFile={removePendingAttachmentFile}
                                                onStageRemoval={(attachment) => stageAttachmentRemoval(attachment.id)}
                                                attachmentSaveError={attachmentSaveError}
                                                attachmentSyncInProgress={attachmentSyncInProgress}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </TabPanel>
                            {initValues && initValues?.id !== emptyGuid && (
                                <TabPanel value={'lineItems'}>
                                    <QuoteLineItemsView quote={initValues} />
                                </TabPanel>
                            )}
                        </TabContext>
                    </FormSection>
                </Grid>
            </Grid>
        </>
    );
};
